import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import CreateWarehouseBottomSheet from '@features/warehouses/components/create/CreateWarehouseBottomSheet';
import { container } from '@infrastructure/container';
import { Warehouse } from '@domain/inventory/models/Warehouse';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import { Icon, IconSize } from '@shared/icons';
import OnboardingOverlayCard from '../../shared/OnboardingOverlayCard';
import OnboardingEntityList from '../../shared/OnboardingEntityList';
import {
  getConnectionTypeLabel,
  getPrimaryConnectionType,
} from './warehouseOnboarding.utils';

interface WarehouseOnboardingListStepProps {
  warehouses: Warehouse[];
  onDone: () => void;
}

const WarehouseOnboardingListStep: React.FC<WarehouseOnboardingListStepProps> = ({
  warehouses,
  onDone,
}) => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data: connectors } = useQuery({
    queryKey: ['connectors', 'wms'],
    queryFn: async () => {
      const result = await container.marketplaceRepository.fetchConnectors('wms');
      if (result.status === 'success') return result.data;
      return [];
    },
  });

  return (
    <>
      <OnboardingOverlayCard
        title="Add your warehouses"
        footer={
          <Button variant="contained" onClick={onDone}>
            I&apos;m done
          </Button>
        }
      >
        <Typography variant="body2">
          Add the warehouses you use so we can keep your stock levels up to date.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <OnboardingEntityList
            interactive={false}
            items={warehouses.map((warehouse) => {
              const sourceType = getPrimaryConnectionType(warehouse);
              const typeLabel = getConnectionTypeLabel(sourceType);
              const connectorImage =
                sourceType &&
                connectors?.find((connector) => (connector.type as string) === sourceType)
                  ?.image;

              return {
                id: warehouse.id,
                primary: typeLabel,
                secondary: warehouse.name,
                avatarSrc: connectorImage || undefined,
                avatarFallback: (
                  <Icon
                    icon={
                      sourceType === WarehouseConnectionType.FILE ? 'File01' : 'Package01'
                    }
                    size={IconSize.MEDIUM}
                  />
                ),
              };
            })}
          />

          <Box>
            <Button
              variant="text"
              onClick={() => setIsCreateOpen(true)}
              startIcon={<Icon icon="Plus01" size={IconSize.MEDIUM} />}
            >
              Add warehouse
            </Button>
          </Box>
        </Box>
      </OnboardingOverlayCard>

      <CreateWarehouseBottomSheet
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        syncUrl={false}
      />
    </>
  );
};

export default WarehouseOnboardingListStep;
